import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { AiChat } from '@openx/components-core/lib/AiChat/AiChat';
import { type Session, SessionList } from '@openx/components-core/lib/AiChat/components/SessionList';
import { getMessageId } from '@openx/components-core/lib/AiChat/messageId';
import type { Message } from '@openx/components-core/lib/AiChat/types';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../utils/context/DealContext';
import { getSendMessageInlineErrorMessage } from '../utils/hooks/troubleshooting/troubleshootingAgentSendMessageErrors';
import { DealTroubleshootingLegalNoticeButton } from './components/DealTroubleshootingLegalNoticeButton';
import { DealTroubleshootingLegalNoticeDrawer } from './components/DealTroubleshootingLegalNoticeDrawer';
import {
  DealTroubleshootingMessageActions,
  type TroubleshootingMessage,
} from './components/DealTroubleshootingMessageActions';
import { SUGGESTION_OPTIONS, TEMPORARY_CHAT_ID } from './constants';
import {
  getActiveTurnDeadline,
  getEventsLabels,
  isDealTroubleshootingAgentThinking,
} from './dealTroubleshootingThinking';
import { useDealTroubleshootingMessageActions } from './useDealTroubleshootingMessageActions';

type UserMessage = Extract<Message, { role: 'user' }>;

const sessionHistoryQueryKey = (dealId: string, sessionId: string) => [
  QueryKeys.DEAL_TROUBLESHOOTING_AGENT,
  'sessionHistory',
  dealId,
  sessionId,
];

const mergeMissingUserMessages = (messages: Message[], userMessages: UserMessage[]) => {
  const existingUserMessageTexts = new Set(
    messages.filter((message): message is UserMessage => message.role === 'user').map(message => message.text)
  );
  const missingUserMessages = userMessages.filter(message => !existingUserMessageTexts.has(message.text));

  return [...messages, ...missingUserMessages].sort((firstMessage, secondMessage) => {
    const firstTimestamp = Date.parse(firstMessage.timestamp);
    const secondTimestamp = Date.parse(secondMessage.timestamp);

    if (Number.isNaN(firstTimestamp) || Number.isNaN(secondTimestamp)) {
      return 0;
    }

    return firstTimestamp - secondTimestamp;
  });
};

export function DealTroubleshootingAgent({ dealId }: { dealId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    useRefreshAuthToken,
    useDealTroubleshootingAgentSessionList,
    useDealTroubleshootingAgentSessionHistory,
    useDealTroubleshootingAgentSendMessage,
    useDealTroubleshootingAgentMessageMutationState,
  } = useDealContext();

  const refreshToken = useRefreshAuthToken();

  const {
    actionsState,
    submittingMessageId,
    handleThumbUp,
    handleThumbDown,
    handleSubmitFeedback,
    handleCancelFeedback,
  } = useDealTroubleshootingMessageActions(dealId);

  // This ref is used to track if switching to newly created session is necessary just after mutation is settled.
  const isTemporaryChat = useRef(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string>(TEMPORARY_CHAT_ID);
  const [isLegalNoticeOpen, setLegalNoticeOpen] = useState(false);
  const [isProcessingTimedOut, setProcessingTimedOut] = useState(false);

  const sendMessageMutation = useDealTroubleshootingAgentSendMessage(dealId, selectedSessionId);
  const sendMessageMutationState = useDealTroubleshootingAgentMessageMutationState(dealId, selectedSessionId);
  const [sendMessageErrors, setSendMessageErrors] = useState<Record<string, Error>>({});

  const { data: sessionListData, isLoading: isSessionListLoading } = useDealTroubleshootingAgentSessionList(dealId);

  const { data: sessionHistory, isLoading: isSessionHistoryLoading } = useDealTroubleshootingAgentSessionHistory({
    dealId,
    sessionId: selectedSessionId,
    enabled: selectedSessionId !== TEMPORARY_CHAT_ID,
    refetchInterval: query => {
      if (sendMessageMutationState.length > 0) {
        return false;
      }

      return isDealTroubleshootingAgentThinking(query.state.data?.messages ?? [], false) ? 2000 : false;
    },
  });

  const sendMessage = useCallback(
    async (message: string) => {
      const sentFromSessionId = selectedSessionId;
      setSendMessageErrors(({ [sentFromSessionId]: _cleared, ...rest }) => rest);

      const sentAt = new Date().toISOString();
      const sentUserMessage: UserMessage = {
        role: 'user',
        text: message,
        timestamp: sentAt,
        invocation_id: sentAt,
      };
      const messagesWithSentUserMessage = [...(sessionHistory?.messages ?? []), sentUserMessage];

      // Seed query cache with sent message for perceived performance improvement
      queryClient.setQueryData(sessionHistoryQueryKey(dealId, selectedSessionId), {
        messages: messagesWithSentUserMessage,
      });

      try {
        await refreshToken();
      } catch {
        // Proceed
      }

      try {
        if (selectedSessionId === TEMPORARY_CHAT_ID) {
          const response = await sendMessageMutation.mutateAsync({ message, prefetchMessages: true });
          if (isTemporaryChat.current) {
            const temporaryUserMessages = messagesWithSentUserMessage.filter(
              (sessionMessage): sessionMessage is UserMessage => sessionMessage.role === 'user'
            );
            const responseSessionHistory = queryClient.getQueryData<{ messages: Message[] }>(
              sessionHistoryQueryKey(dealId, response.session_id)
            );

            queryClient.setQueryData(sessionHistoryQueryKey(dealId, response.session_id), {
              messages: mergeMissingUserMessages(responseSessionHistory?.messages ?? [], temporaryUserMessages),
            });

            setSelectedSessionId(response.session_id);
            isTemporaryChat.current = false;
          }
        } else {
          await sendMessageMutation.mutateAsync({ message, sessionId: selectedSessionId });
        }
      } catch (error) {
        // Swallow rejection so AiChat stays interactive for retry; error is shown inline.
        setSendMessageErrors(errors => ({
          ...errors,
          [sentFromSessionId]: error instanceof Error ? error : new Error(String(error)),
        }));
      }
    },
    [selectedSessionId, sendMessageMutation, refreshToken, dealId, queryClient, sessionHistory?.messages]
  );

  const handleNewSessionClick = useCallback(() => {
    if (sessionHistory && sessionHistory.messages.length > 0) {
      setSelectedSessionId(TEMPORARY_CHAT_ID);
      isTemporaryChat.current = true;
    }
  }, [sessionHistory]);

  const handleSessionClick = useCallback((session: Session) => {
    setSelectedSessionId(session.session_id);
    isTemporaryChat.current = false;
  }, []);

  const sessionList = sessionListData?.sessions ?? [];
  const messages = sessionHistory?.messages ?? [];
  const mutationPending = sendMessageMutationState.length > 0;
  const sendMessageError = sendMessageErrors[selectedSessionId];
  const sendMessageFailed = !mutationPending && !!sendMessageError;

  const activeTurnDeadline = useMemo(() => getActiveTurnDeadline(messages), [messages]);

  // Query polling stops at the deadline without necessarily rerendering the component.
  // This timer keeps the UI processing state in sync with the polling state.
  useEffect(() => {
    setProcessingTimedOut(false);

    if (mutationPending || sendMessageFailed || activeTurnDeadline === null) {
      return;
    }

    const remaining = activeTurnDeadline - Date.now();
    if (remaining <= 0) {
      setProcessingTimedOut(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setProcessingTimedOut(true);
    }, remaining);

    return () => window.clearTimeout(timeoutId);
  }, [activeTurnDeadline, mutationPending, sendMessageFailed]);

  const isThinking = useMemo(
    () =>
      !sendMessageFailed &&
      (mutationPending || (!isProcessingTimedOut && isDealTroubleshootingAgentThinking(messages, false))),
    [messages, mutationPending, sendMessageFailed, isProcessingTimedOut]
  );

  const thinkingProcessMessages = useMemo(() => {
    if (!isThinking) {
      return [];
    }
    return getEventsLabels(messages, t);
  }, [messages, isThinking, t]);

  const sendMessageErrorMessage =
    sendMessageError && sendMessageFailed ? getSendMessageInlineErrorMessage(sendMessageError, t) : undefined;

  const processingTimeoutMessage =
    !mutationPending && !sendMessageFailed && isProcessingTimedOut
      ? t('Something went wrong while processing your request. Please try again later.')
      : undefined;
  const errorMessage = sendMessageErrorMessage ?? processingTimeoutMessage;

  return (
    <Stack direction={'row'} sx={{ height: '100%', overflow: 'hidden', position: 'relative' }}>
      <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: theme => theme.zIndex.drawer + 1 }}>
        <SessionList
          footerContent={
            <DealTroubleshootingLegalNoticeButton
              open={isLegalNoticeOpen}
              onClick={() => setLegalNoticeOpen(currentOpen => !currentOpen)}
            />
          }
          loading={isSessionListLoading}
          sessionList={sessionList}
          onSessionClick={handleSessionClick}
          onNewSessionClick={handleNewSessionClick}
          onCollapsedChange={() => setLegalNoticeOpen(false)}
          selectedSessionId={selectedSessionId}
        />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0, position: 'relative' }}>
        <AiChat
          key={selectedSessionId}
          headerText={t('Troubleshoot Your Deal')}
          subHeaderText={t('Pick a Quick Chip or Type Your Question Below.')}
          messageActions={(message, widgets) => (
            <DealTroubleshootingMessageActions
              message={message as TroubleshootingMessage}
              widgets={widgets}
              msgState={actionsState[getMessageId(message)]}
              isSubmitting={submittingMessageId === getMessageId(message)}
              onThumbUp={handleThumbUp}
              onThumbDown={handleThumbDown}
              onSubmitFeedback={handleSubmitFeedback}
              onCancelFeedback={handleCancelFeedback}
            />
          )}
          isThinking={isThinking}
          thinkingProcessMessages={thinkingProcessMessages}
          isLoading={isSessionHistoryLoading}
          messages={messages}
          sendMessage={sendMessage}
          suggestions={SUGGESTION_OPTIONS}
          errorMessage={errorMessage}
        />
        <DealTroubleshootingLegalNoticeDrawer open={isLegalNoticeOpen} onClose={() => setLegalNoticeOpen(false)} />
      </Box>
    </Stack>
  );
}
